import React, { useState, useEffect, useCallback } from 'react';
import { RotateCw, ChevronLeft, ChevronRight, Award, Eye } from 'lucide-react';
import { calculateSM2, QUALITY_SCORES } from '../utils/srsEngine';

export default function FlashcardViewer({ deckTitle, flashcards = [], onReviewComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [cards, setCards] = useState(flashcards);
  const [showClozeAnswer, setShowClozeAnswer] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);

  useEffect(() => {
    setCards(flashcards);
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionCompleted(false);
  }, [flashcards]);

  const currentCard = cards[currentIndex];

  const handleNext = useCallback(() => {
    setIsFlipped(false);
    setShowClozeAnswer(false);
    if (currentIndex < cards.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setSessionCompleted(true);
    }
  }, [currentIndex, cards.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setIsFlipped(false);
      setShowClozeAnswer(false);
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const handleRating = useCallback((score) => {
    if (!currentCard) return;

    const updatedSrs = calculateSM2(currentCard.srs, score);
    const updatedCards = [...cards];
    updatedCards[currentIndex] = {
      ...currentCard,
      srs: updatedSrs,
    };

    setCards(updatedCards);

    if (onReviewComplete) {
      onReviewComplete(updatedCards[currentIndex]);
    }

    handleNext();
  }, [currentCard, cards, currentIndex, onReviewComplete, handleNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (sessionCompleted || !currentCard) return;

      if (e.code === 'Space') {
        e.preventDefault();
        setIsFlipped((prev) => !prev);
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (isFlipped) {
        if (e.key === '1') handleRating(QUALITY_SCORES.AGAIN);
        if (e.key === '2') handleRating(QUALITY_SCORES.HARD);
        if (e.key === '3') handleRating(QUALITY_SCORES.GOOD);
        if (e.key === '4') handleRating(QUALITY_SCORES.EASY);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFlipped, sessionCompleted, currentCard, handleNext, handlePrev, handleRating]);

  if (!cards || cards.length === 0) {
    return (
      <div className="bg-dark-700/50 border border-white/10 rounded-2xl p-8 text-center text-white/50 backdrop-blur-xl">
        No flashcards available in this deck.
      </div>
    );
  }

  if (sessionCompleted) {
    return (
      <div className="bg-dark-700/60 border border-white/10 rounded-2xl p-8 text-center max-w-lg mx-auto shadow-xl backdrop-blur-xl">
        <div className="w-16 h-16 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4">
          <Award className="w-8 h-8" />
        </div>
        <h3 className="font-display text-2xl font-bold text-white">Session Complete! 🎉</h3>
        <p className="text-sm text-white/60 mt-2">
          You have reviewed all {cards.length} flashcards in <span className="font-semibold text-white">"{deckTitle}"</span>.
        </p>

        <div className="mt-6 flex justify-center gap-3">
          <button
            onClick={() => {
              setCurrentIndex(0);
              setIsFlipped(false);
              setSessionCompleted(false);
            }}
            className="btn-primary text-sm font-semibold shadow-sm"
          >
            Review Deck Again
          </button>
        </div>
      </div>
    );
  }

  // Cloze formatting helper
  const renderCloze = (text) => {
    if (!text) return null;
    const parts = text.split(/\[c1::(.*?)\]/g);
    return (
      <span>
        {parts.map((part, i) =>
          i % 2 === 1 ? (
            <span
              key={i}
              className={`px-2 py-0.5 rounded font-mono font-semibold transition-all ${
                isFlipped || showClozeAnswer
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  : 'bg-dark-500 text-transparent select-none cursor-pointer'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowClozeAnswer(true);
              }}
              title="Click to reveal cloze word"
            >
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header Info Bar */}
      <div className="flex items-center justify-between mb-3 text-xs text-white/50 font-medium">
        <span>Card {currentIndex + 1} of {cards.length}</span>
        <div className="flex items-center gap-2">
          {currentCard.sourcePage && (
            <span className="px-2 py-0.5 bg-dark-600/80 text-white/70 border border-white/10 rounded text-[11px] font-mono">
              Source: Page / Slide {currentCard.sourcePage}
            </span>
          )}
          {currentCard.tags?.map((tag, idx) => (
            <span key={idx} className="px-2 py-0.5 bg-saffron-500/15 text-saffron-300 border border-saffron-500/20 rounded text-[11px]">
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1.5 bg-dark-600 rounded-full mb-6 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-saffron-500 to-amber-400 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / cards.length) * 100}%` }}
        />
      </div>

      {/* 3D Flip Card Container */}
      <div
        onClick={() => setIsFlipped(!isFlipped)}
        className="w-full min-h-[320px] cursor-pointer perspective-1000 mb-6 group"
      >
        <div
          className={`relative w-full h-full min-h-[320px] rounded-2xl border transition-all duration-500 transform-style-3d shadow-lg ${
            isFlipped
              ? 'bg-dark-800 border-saffron-500/40 text-white [transform:rotateY(180deg)]'
              : 'bg-dark-700 border-white/10 text-white hover:border-white/20'
          }`}
        >
          {/* FRONT side */}
          <div className={`absolute inset-0 p-8 flex flex-col justify-between backface-hidden rounded-2xl transition-opacity duration-300 ${
            !isFlipped ? 'opacity-100 visible z-10' : 'opacity-0 invisible z-0'
          }`}>
            <div className="flex justify-between items-start">
              <span className="text-xs font-semibold uppercase tracking-wider text-saffron-400 bg-saffron-500/15 border border-saffron-500/20 px-2.5 py-1 rounded-lg">
                {currentCard.type === 'cloze' ? 'Cloze Deletion' : 'Active Recall Question'}
              </span>
              <span className="text-xs text-white/40 flex items-center gap-1">
                <RotateCw className="w-3.5 h-3.5" /> Click or Space to flip
              </span>
            </div>

            <div className="my-auto text-center px-2">
              {currentCard.type === 'cloze' && currentCard.clozeText ? (
                <p className="text-lg md:text-xl font-medium leading-relaxed text-white">
                  {renderCloze(currentCard.clozeText)}
                </p>
              ) : (
                <p className="text-lg md:text-xl font-medium leading-relaxed text-white">
                  {currentCard.front}
                </p>
              )}
            </div>

            <div className="text-center text-xs text-white/40">
              Press <kbd className="px-1.5 py-0.5 bg-dark-600 border border-white/10 rounded text-white/80 font-mono text-[11px]">Space</kbd> to reveal answer
            </div>
          </div>

          {/* BACK side */}
          <div className={`absolute inset-0 p-8 flex flex-col justify-between backface-hidden [transform:rotateY(180deg)] rounded-2xl transition-opacity duration-300 ${
            isFlipped ? 'opacity-100 visible z-10' : 'opacity-0 invisible z-0'
          }`}>
            <div className="flex justify-between items-start">
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 px-2.5 py-1 rounded-lg">
                Answer / Key Explanation
              </span>
              <span className="text-xs text-white/40">Card Answer</span>
            </div>

            <div className="my-auto text-center px-2">
              <p className="text-lg md:text-xl font-medium leading-relaxed text-white">
                {currentCard.back}
              </p>
            </div>

            <div className="text-center text-xs text-white/40">
              Rate recall difficulty below to schedule spaced repetition
            </div>
          </div>
        </div>
      </div>

      {/* Review & Navigation Buttons */}
      <div className="space-y-4">
        {isFlipped ? (
          <div>
            <p className="text-xs font-semibold text-white/50 text-center mb-2.5">
              Rate your recall difficulty (SM-2 Spaced Repetition):
            </p>
            <div className="grid grid-cols-4 gap-2.5">
              <button
                onClick={() => handleRating(QUALITY_SCORES.AGAIN)}
                className="py-2.5 px-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 text-xs font-semibold rounded-xl flex flex-col items-center gap-0.5 transition-all"
              >
                <span>1. Again</span>
                <span className="text-[10px] opacity-75">1 day</span>
              </button>

              <button
                onClick={() => handleRating(QUALITY_SCORES.HARD)}
                className="py-2.5 px-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 text-xs font-semibold rounded-xl flex flex-col items-center gap-0.5 transition-all"
              >
                <span>2. Hard</span>
                <span className="text-[10px] opacity-75">2 days</span>
              </button>

              <button
                onClick={() => handleRating(QUALITY_SCORES.GOOD)}
                className="py-2.5 px-2 bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-xs font-semibold rounded-xl flex flex-col items-center gap-0.5 transition-all"
              >
                <span>3. Good</span>
                <span className="text-[10px] opacity-75">4 days</span>
              </button>

              <button
                onClick={() => handleRating(QUALITY_SCORES.EASY)}
                className="py-2.5 px-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-xs font-semibold rounded-xl flex flex-col items-center gap-0.5 transition-all"
              >
                <span>4. Easy</span>
                <span className="text-[10px] opacity-75">7 days</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center gap-3">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="btn-secondary text-xs font-semibold disabled:opacity-30 flex items-center gap-1 py-2.5 px-4"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <button
              onClick={() => setIsFlipped(true)}
              className="btn-primary text-xs font-semibold shadow-sm flex items-center gap-1.5 py-2.5 px-6"
            >
              <Eye className="w-4 h-4" /> Show Answer
            </button>

            <button
              onClick={handleNext}
              className="btn-secondary text-xs font-semibold flex items-center gap-1 py-2.5 px-4"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
